<template>
  <ModalComponent v-model:dialogVisible="showModal">
    <template #modalContent>
      <CropperComponent
        :imageUrl="imageUrl"
        :aspect-ratio="aspectRatio"
        :view-mode="viewMode"
        @cropped="handleCroppedImage"
        @cancel="handleCancel"
        @close-dialog="handleCloseDialog"
      />
    </template>
  </ModalComponent>
  <div
    class="flex flex-col items-center w-full gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl shadow-blue-gray-900/5 bg-clip-border"
  >
    <div
      class="relative flex flex-col w-full font-regular p-8 portrait:px-2 portrait:py-5 py-10 text-gray-900 max-w-5xl dark:text-gray-200"
    >
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5 mb-6 items-center portrait:items-start"
      >
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-start justify-start landscape:mr-2"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-blue-gray-600/10 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 ease-in-out transform hover:scale-105"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
          <h3
            class="text-[clamp(18px,2vw,24px)] font-medium dark:text-gray-200"
          >
            Edit Product
          </h3>
        </div>
        <a
          href=""
          class="w-full landscape:max-w-fit flex gap-2 items-center justify-center px-5 py-4 bg-blue-gray-500/10 rounded-lg text-[14px] border-blue-gray-600/10 font-medium h-min dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-blue-gray-500/30"
          ><i
            class="fa-regular fa-circle-play dark:text-gray-200 transition-transform duration-300 ease-in-out transform hover:rotate-12"
          ></i
          ><span>Watch Video Tutorial</span></a
        >
      </nav>

      <!-- Product form starts here -->
      <div
        class="w-full border-[1px] bg-white dark:bg-gray-700 border-spacing-2 border-gray-900/10 flex flex-col h-full rounded-lg relative"
      >
        <h4
          class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex rounded-t-lg text-[18px] font-medium"
        >
          Product Status
        </h4>
        <form class="flex flex-col gap-4 mt-4 py-6">
          <div class="mb-4 px-6">
            <div class="flex items-center justify-between">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                <p
                  class="block antialiased font-medium leading-normal dark:text-gray-200 text-gray-900"
                >
                  Product Status
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-900"
                  ></i>
                  <p class="text-sm">
                    This determines if your product is visible to your customers
                  </p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  id="switch"
                  type="checkbox"
                  v-model="productFormData.visible"
                  @input="handleEditedField(true, 'visible')"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 dark:checked:bg-sub-900 peer-checked:border-main-900 peer-checked:before:bg-main-900"
                  defaultChecked
                />
                <label
                  htmlFor="switch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 dark:peer-checked:border-sub-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Product Details
          </h4>
          <div class="mb-4 px-6">
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Product Name<span
                class="text-red-900 dark:text-red-500 text-[14px]"
                >*</span
              >
            </p>
            <div class="relative h-14 w-full">
              <input
                type="text"
                placeholder="Product 1"
                v-model="productFormData.productName"
                @input="handleEditedField(true, 'productname')"
                :class="[
                  'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700',
                  formErrors.productName
                    ? 'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60'
                    : '',
                ]"
              />
            </div>
          </div>
          <div class="mb-4 px-6">
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Product Cost<span
                class="text-red-900 dark:text-red-500 text-[14px]"
                >*</span
              >
            </p>
            <div class="relative w-full h-14">
              <div
                :class="{
                  'absolute flex flex-col justify-center items-center w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-200/80 top-2/4 left-3 -translate-y-2/4': true,
                  '!text-red-800/80 dark:!text-red-500/80':
                    formErrors.productCost,
                }"
              >
                <span class="text-lg font-medium">{{
                  getCurrencySymbol(storeCurrencyId)
                }}</span>
              </div>
              <input
                type="number"
                v-model="productFormData.productCost"
                @input="handleEditedField(true, 'productcost')"
                placeholder="0.00"
                step="0.01"
                min="0"
                inputmode="decimal"
                :class="{
                  'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-700 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 !pl-9': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    formErrors.productCost,
                }"
              />
            </div>
          </div>
          <div class="mb-4 px-6" v-if="editor">
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Product Description (Optional)
            </p>
            <div
              :class="{
                'w-full overflow-hidden rounded-xl border border-blue-gray-200 bg-white disabled:border-0 disabled:bg-blue-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:disabled:bg-gray-700': true,
                '!border-2 !border-gray-900 dark:!border-gray-300':
                  isDescriptionFocused,
              }"
            >
              <div id="hs-editor-tiptap">
                <div
                  class="m-1 flex items-center gap-0.5 rounded-lg bg-gray-100 p-1 dark:bg-gray-700"
                >
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-bold
                    @click="editor.chain().focus().toggleBold().run()"
                  >
                    <i
                      class="fa-solid fa-bold text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-italic
                    @click="editor.chain().focus().toggleItalic().run()"
                  >
                    <i
                      class="fa-solid fa-italic text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-underline
                    @click="editor.chain().focus().toggleUnderline().run()"
                  >
                    <i
                      class="fa-solid fa-underline text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-strike
                    @click="editor.chain().focus().toggleStrike().run()"
                  >
                    <i
                      class="fa-solid fa-strikethrough text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-link
                    @click="
                      () => {
                        const url = window.prompt('URL');
                        editor
                          .chain()
                          .focus()
                          .extendMarkRange('link')
                          .setLink({ href: url })
                          .run();
                      }
                    "
                  >
                    <i
                      class="fa-solid fa-link text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-ol
                    @click="editor.chain().focus().toggleOrderedList().run()"
                  >
                    <i
                      class="fa-solid fa-list-ol text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-ul
                    @click="editor.chain().focus().toggleBulletList().run()"
                  >
                    <i
                      class="fa-solid fa-list-ul text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-blockquote
                    @click="editor.chain().focus().toggleBlockquote().run()"
                  >
                    <i
                      class="fa-solid fa-quote-left text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-code
                    @click="addImage"
                  >
                    <i
                      class="fa-solid fa-image text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                  <button
                    class="relative grid h-10 max-h-[40px] w-10 max-w-[40px] select-none place-items-center rounded-lg text-center align-middle font-sans text-xs font-medium uppercase text-gray-900 transition-all hover:bg-gray-900/10 active:bg-gray-900/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:text-gray-200 dark:hover:bg-gray-600/10 dark:active:bg-gray-600/20"
                    type="button"
                    data-hs-editor-code
                    @click="addVideo"
                  >
                    <i
                      class="fa-solid fa-file-video text-lg text-gray-900/80 dark:text-gray-200/80"
                    ></i>
                  </button>
                </div>
                <editor-content
                  :editor="editor"
                  class="editor-content p-5 [&_*]:outline-none [&_.tiptap.ProseMirror]:min-h-[180px] !w-full text-blue-gray-700 dark:text-gray-200"
                  @focusin="handleFocus"
                  @focusout="handleBlur"
                />
                <div
                  :class="{
                    'character-count flex flex-row gap-2 px-2 py-1 items-center justify-center text-[14px] text-blue-gray-700': true,
                    'character-count--warning':
                      editor.storage.characterCount.characters() === limit,
                  }"
                >
                  <svg height="20" width="20" viewBox="0 0 20 20">
                    <!-- Outer Circle -->
                    <circle
                      r="10"
                      cx="10"
                      cy="10"
                      fill="#e9ecef"
                      class="dark:fill-sub-200"
                    />
                    <!-- Progress Circle -->
                    <circle
                      r="5"
                      cx="10"
                      cy="10"
                      fill="transparent"
                      stroke="currentColor"
                      stroke-width="10"
                      :stroke-dasharray="`calc(${percentage} * 31.4 / 100) 31.4`"
                      transform="rotate(-90) translate(-20)"
                    />
                    <!-- Inner Circle -->
                    <circle
                      r="6"
                      cx="10"
                      cy="10"
                      fill="white"
                      class="dark:fill-sub-900"
                    />
                  </svg>

                  <span class="dark:text-gray-200"
                    >{{ editor.storage.characterCount.characters() }} /
                    {{ limit }} characters</span
                  >
                  <span class="dark:text-gray-200"
                    >{{ editor.storage.characterCount.words() }} words</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="mb-4 px-6">
            <p
              class="block mb-4 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200 text-sm sm:text-base"
            >
              Product Category (Optional)
            </p>
            <div class="relative h-14 w-full" ref="categoryDropdown">
              <!-- Dropdown Button -->
              <div
                class="flex items-center justify-between h-14 bg-white dark:bg-gray-700 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 font-normal rounded-lg cursor-pointer border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-300"
                @click="toggleCategoryMenu"
              >
                <span
                  class="sBtn-text text-gray-700 dark:text-gray-200 truncate"
                >
                  {{
                    productFormData.selectedCategory &&
                    productFormData.selectedCategory.length > 0
                      ? productFormData.selectedCategory.join(", ")
                      : "Select Product Categories"
                  }}
                </span>
                <!-- Dropdown Arrow -->
                <i
                  :class="{
                    'transform rotate-180': isCategoryMenuActive,
                    'fa-solid fa-chevron-down': true,
                  }"
                  class="transition-transform duration-300 text-gray-600 dark:text-gray-300"
                ></i>
              </div>

              <!-- Dropdown Menu -->
              <ul
                v-if="isCategoryMenuActive"
                class="absolute z-10 w-full mt-2 max-h-72 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg text-gray-700 dark:text-gray-200"
              >
                <li
                  v-if="!productFormData?.selectedCategory?.length > 0"
                  class="text-center text-sm text-gray-500 dark:text-gray-400 p-3"
                >
                  No categories available.
                </li>
                <li
                  v-for="category in categories"
                  :key="category"
                  class="flex items-center h-14 px-4 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-300"
                  :class="{
                    'bg-blue-100 dark:bg-blue-800/30':
                      productFormData.selectedCategory.includes(category),
                  }"
                >
                  <!-- Checkbox -->
                  <input
                    type="checkbox"
                    :id="category"
                    :value="category"
                    @input="handleEditedField(true, 'productcategory')"
                    v-model="productFormData.selectedCategory"
                    class="form-checkbox h-5 w-5 text-blue-600 dark:text-blue-400 transition duration-150 ease-in-out"
                  />
                  <label
                    :for="category"
                    class="ml-3 flex items-center cursor-pointer"
                  >
                    <span
                      class="option-text text-sm sm:text-base text-gray-800 dark:text-gray-200"
                    >
                      {{ category }}
                    </span>
                  </label>
                </li>
              </ul>
            </div>
            <RouterLink to="/product-settings">
              <div
                class="flex flex-row justify-start items-center gap-3 text-sm font-medium text-main-800 dark:text-sub-600 pt-4 mx-2 hover:text-main-900 dark:hover:text-main-300 transition-colors duration-300"
              >
                <i class="fa-solid fa-plus text-main-800 dark:text-sub-600"></i>
                <span>Add New Category</span>
              </div>
            </RouterLink>
          </div>
          <div class="mb-4 px-6">
            <p
              class="block mb-4 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200 text-sm sm:text-base"
            >
              Product Size (Optional)
            </p>
            <div class="relative h-14 w-full" ref="sizeDropdown">
              <div
                class="flex items-center justify-between h-14 bg-white dark:bg-gray-700 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 font-normal rounded-lg cursor-pointer border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-300"
                @click="toggleMenu"
              >
                <span
                  class="sBtn-text text-gray-700 dark:text-gray-200 truncate"
                >
                  {{
                    productFormData.selectedSize &&
                    productFormData.selectedSize.length > 0
                      ? productFormData.selectedSize.join(", ")
                      : "Select Product Size"
                  }}
                </span>
                <i
                  :class="{
                    'transform rotate-180': isActive,
                    'fa-solid fa-chevron-down': true,
                  }"
                  class="transition-transform duration-300 text-gray-600 dark:text-gray-300"
                ></i>
              </div>
              <ul
                v-if="isActive"
                class="absolute z-10 w-full mt-2 max-h-72 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg text-gray-700 dark:text-gray-200"
              >
                <li
                  v-if="!sizes"
                  class="text-center text-sm text-gray-500 dark:text-gray-400 p-3"
                >
                  No sizes available.
                </li>
                <li
                  v-for="size in sizes"
                  :key="size"
                  class="flex items-center h-14 px-4 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-300"
                  :class="{
                    'bg-blue-100 dark:bg-blue-800/30':
                      productFormData.selectedSize.includes(size),
                  }"
                >
                  <input
                    type="checkbox"
                    :id="size"
                    :value="size"
                    @input="handleEditedField(true, 'productsize')"
                    v-model="productFormData.selectedSize"
                    class="form-checkbox h-5 w-5 text-blue-600 dark:text-blue-400 transition duration-150 ease-in-out"
                  />
                  <label
                    :for="size"
                    class="ml-2 flex items-center cursor-pointer"
                  >
                    <span
                      class="option-text text-sm sm:text-base text-gray-800 dark:text-gray-200"
                      >{{ size }}</span
                    >
                  </label>
                </li>
              </ul>
            </div>
            <RouterLink to="/product-settings">
              <div
                class="flex flex-row justify-start items-center gap-3 text-sm font-medium text-main-800 dark:text-sub-600 pt-4 mx-2 hover:text-main-900 dark:hover:text-main-300 transition-colors duration-300"
              >
                <i class="fa-solid fa-plus text-main-800 dark:text-sub-600"></i>
                <span>Add New Size</span>
              </div>
            </RouterLink>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Product Image
          </h4>
          <div class="mb-4 px-6 pt-6">
            <div class="mb-4 flex flex-col items-start m-0 p-0">
              <p
                class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200 text-sm sm:text-base"
              >
                Product Image Upload<span
                  class="text-red-600 dark:text-red-500 text-sm"
                  >*</span
                >
              </p>
              <p class="text-xs text-gray-600 dark:text-gray-400">
                <i
                  class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                ></i>
                Recommended dimension: 1028 x 1028 px, Max file size: 5mb
              </p>
            </div>
            <div class="relative w-full h-auto">
              <div class="flex flex-col gap-1 mb-2">
                <p
                  v-if="exceedMaxCount"
                  class="text-red-600 dark:text-red-400 text-sm"
                >
                  Maximum number of pictures for your current plan is
                  <strong>{{ maxCount }}</strong
                  >.
                  <RouterLink to="/upgrade-plan">
                    <button
                      class="px-2 py-1 bg-blue-gray-500/10 hover:bg-blue-gray-500/20 rounded text-sm border border-blue-gray-500/20 font-medium text-gray-800 dark:text-gray-200 transition-colors duration-300"
                    >
                      Upgrade Plan
                    </button>
                  </RouterLink>
                </p>
                <p
                  v-if="exceedFileSize"
                  class="text-red-600 dark:text-red-400 text-sm"
                >
                  Maximum file size: <strong>{{ maxFileSize }} MB</strong>
                </p>
                <p
                  v-if="invalidFileType"
                  class="text-red-600 dark:text-red-400 text-sm"
                >
                  Allowed file types: <strong>jpg, png</strong>
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <div
                  v-for="(file, index) in productFormData.productImages"
                  :key="index"
                  class="h-[80px] aspect-square rounded-lg border-dashed border-main-800 dark:border-main-400 flex flex-col items-center justify-center border relative p-1"
                >
                  <img
                    :src="getFileUrl(file)"
                    @error="
                      (event) =>
                        (event.target.src = '/landscape-placeholder.svg')
                    "
                    class="h-full w-full object-cover object-center rounded-lg"
                    alt="Product Image"
                  />
                  <button
                    @click="removeproductImage(index)"
                    data-dismissible-target="alert"
                    class="!absolute p-2 bg-white/80 dark:bg-gray-700/80 h-6 w-6 top-2 right-2 select-none rounded-lg text-center align-middle font-medium uppercase text-gray-800 dark:text-gray-200 transition-all hover:bg-gray-800/20 active:bg-gray-800/30 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none flex flex-col justify-center items-center"
                    type="button"
                  >
                    <i
                      class="fa-solid fa-trash text-[16px] text-main-900 dark:text-red-600"
                    ></i>
                  </button>
                </div>
                <label
                  v-if="productFormData.productImages.length < maxCount"
                  class="w-[80px] aspect-square rounded-lg border-dashed border-gray-400 dark:border-gray-500 flex flex-col items-center justify-center border hover:border-main-800 dark:hover:border-main-400 transition-colors duration-300 cursor-pointer"
                >
                  <i
                    class="fa-solid fa-plus text-gray-400 dark:text-gray-500"
                  ></i>
                  <input
                    type="file"
                    accept="image/*"
                    class="hidden"
                    @change="handleImageUpload"
                  />
                </label>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Product Inventory
          </h4>
          <div class="flex flex-wrap py-4 sm:py-6">
            <div class="mb-4 px-4 sm:px-6 w-full lg:w-1/2">
              <p
                class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200 text-sm sm:text-base"
              >
                Quantity in stock<span class="text-red-600 text-sm">*</span>
              </p>
              <div class="relative h-14 w-full">
                <input
                  type="number"
                  placeholder="0"
                  v-model="productFormData.productQuantity"
                  @input="handleEditedField(true, 'productquantity')"
                  :class="{
                    'peer h-full w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent px-4 py-2.5 text-sm font-normal text-gray-800 dark:text-gray-200 outline-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-2 focus:border-gray-900 dark:focus:border-gray-200 focus:ring-0': true,
                    'border-red-600 dark:border-red-400 bg-red-50/50 dark:bg-red-800/10 text-red-600 dark:text-red-400 placeholder-red-600/60 dark:placeholder-red-400/60 focus:border-red-600 dark:focus:border-red-400':
                      formErrors.stockQuantityError,
                  }"
                />
              </div>
            </div>
            <div class="mb-4 px-4 sm:px-6 w-full lg:w-1/2">
              <p
                class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200 text-sm sm:text-base"
              >
                Max quantity per order
              </p>
              <div class="relative h-14 w-full">
                <input
                  type="number"
                  placeholder="0"
                  v-model="productFormData.maxQuantityPerOrder"
                  @input="handleEditedField(true, 'maxquantity')"
                  :class="{
                    'peer h-full w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent px-4 py-2.5 text-sm font-normal text-gray-800 dark:text-gray-200 outline-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-2 focus:border-gray-900 dark:focus:border-gray-200 focus:ring-0': true,
                    'border-red-600 dark:border-red-400 bg-red-50/50 dark:bg-red-800/10 text-red-600 dark:text-red-400 placeholder-red-600/60 dark:placeholder-red-400/60 focus:border-red-600 dark:focus:border-red-400':
                      formErrors.maxQuantityPerOrderError,
                  }"
                />
              </div>
            </div>
          </div>
          <div
            class="flex flex-row justify-end items-center gap-4 px-4 sm:px-6 py-4"
          >
            <button
              @click="handleSubmitForm"
              :disabled="!Object.keys(this.editedFields).length > 0"
              class="align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-sm sm:text-base py-2 sm:py-3 px-4 sm:px-6 rounded-lg bg-main-800 text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-800/40 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none dark:bg-sub-800 dark:hover:shadow-sub-800/40"
            >
              Update Product
            </button>
            <button
              @click="goback"
              class="select-none rounded-lg border border-main-800 py-2 sm:py-3 px-4 sm:px-6 text-center align-middle text-sm sm:text-base font-semibold text-main-800 transition-all hover:opacity-75 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none dark:border-sub-800 dark:text-sub-800 dark:hover:opacity-75"
            >
              Cancel Action
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-3";
import Placeholder from "@tiptap/extension-placeholder";
import Paragraph from "@tiptap/extension-paragraph";
import StarterKit from "@tiptap/starter-kit";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import Blockquote from "@tiptap/extension-blockquote";
import Document from "@tiptap/extension-document";
import Text from "@tiptap/extension-text";
import Typography from "@tiptap/extension-typography";
import CharacterCount from "@tiptap/extension-character-count";
import ListKeymap from "@tiptap/extension-list-keymap";
import ListItem from "@tiptap/extension-list-item";
import Dropcursor from "@tiptap/extension-dropcursor";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import { mapActions } from "vuex";
import ModalComponent from "@/components/modalComponent.vue";
import CropperComponent from "@/components/cropperComponent.vue";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

export default {
  name: "EditProductView",
  inject: ["getFileUrl"],
  components: {
    EditorContent,
    ModalComponent,
    CropperComponent,
  },
  props: {
    maxFileSize: {
      type: Number,
      default: 5,
    },
    maxCount: {
      type: Number,
      default: 3,
    },
  },
  data() {
    return {
      productFormData: {
        productName: "",
        productCost: "",
        productDescription: "",
        selectedSize: [],
        selectedCategory: [],
        productImages: [],
        productQuantity: "",
        maxQuantityPerOrder: "",
        visible: 0,
      },
      formErrors: {
        productName: false,
        productCost: false,
        stockQuantityError: false,
        maxQuantityPerOrderError: false,
      },
      editedFields: {},
      isActive: false,
      isCategoryMenuActive: false,
      file: null,
      sizes: [],
      categories: [],
      showModal: false,
      imageUrl: "",
      exceedFileSize: false,
      invalidFileType: false,
      exceedMaxCount: false,
      isSizeFocused: false,
      isCategoryFocused: false,
      isDescriptionFocused: false,
      viewMode: 1,
      aspectRatio: 1,
      editor: null,
      limit: 280,
      youtubewidth: "640",
      youtubeheight: "480",
      currencies: [],
      productId: 0,
      originalProductData: null,
      removedImages: [],
    };
  },
  computed: {
    percentage() {
      return Math.round(
        (100 / this.limit) * this.editor.storage.characterCount.characters()
      );
    },
    storeCurrencyId() {
      return this.$store.state.selectedStore.default_currency;
    },
  },
  methods: {
    ...mapActions([
      "getProductCategory",
      "getProductSize",
      "updateProduct",
      "fetchCurrency",
      "fetchProduct",
    ]),

    async fetchProductDetails() {
      try {
        const response = await this.fetchProduct(this.productId);
        if (response.success) {
          const productData = response.product;
          console.log(productData);

          this.productFormData = {
            productName: productData.name || "",
            productCost: productData.cost || "",
            selectedSize: Array.isArray(productData.size)
              ? productData.size
              : [],
            selectedCategory: Array.isArray(productData.category)
              ? productData.category
              : [],
            productImages: productData.images || [],
            productQuantity: productData.stockQuantity || "",
            maxQuantityPerOrder: productData.maxPerOrder || "",
            visible: Boolean(productData.visibility) || false,
          };

          if (this.editor) {
            console.log("Setting editor content...");
            this.editor.commands.setContent(productData.description || "");
          } else {
            console.error("Editor is not initialized yet.");
          }
        } else {
          console.error("Failed to fetch product details:", response.error);
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
    },
    getCurrencySymbol(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_symbol : null;
      }
    },
    getCurrencyCode(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_code : null;
      }
    },
    async getCurrency() {
      const response = await this.fetchCurrency();
      if (response.success) {
        this.currencies = response.currency;
      }
    },
    async handleSubmitForm(event) {
      event.preventDefault();
      if (this.productFormData?.productImages?.length === 0) {
        toast.error("You need to upload an image to proceed");
        return;
      }
      if (this.productFormData?.productCost === "") {
        toast.error("You need to set product cost to proceed");
        return;
      }
      try {
        const formData = new FormData();

        // Append deleted images
        if (this.removedImages?.length > 0) {
          formData.append("deletedImages", JSON.stringify(this.removedImages));
        }

        formData.append("productId", this.productId);

        Object.keys(this.productFormData).forEach((key) => {
          const value = this.productFormData[key];
          if (Array.isArray(value)) {
            value.forEach((item) => formData.append(`${key}[]`, item));
          } else {
            formData.append(key, value);
          }
        });

        for (const [key, value] of formData.entries()) {
          console.log(`Key: ${key}, Value:`, value);
        }

        const updateProductPromise = this.updateProduct(formData).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(data.response);
            }
            return data.response;
          }
        );

        // Show notification using toast
        toast.promise(
          updateProductPromise,
          {
            pending: "Adding product...",
            success: {
              render() {
                return "Product updated successfully!";
              },
              icon: true,
            },
            error: {
              render({ data }) {
                return data || "Failed to add product!";
              },
            },
          },
          {
            autoClose: 3000,
            position: "top-right",
          }
        );
      } catch (error) {
        console.error("Error during submission:", error);
        alert("An error occurred while updating the product.");
      }
    },
    handleEditedField(value, field, subField = null) {
      if (!this.editedFields) {
        this.editedFields = {};
      }
      if (subField) {
        if (!this.editedFields[field]) {
          this.editedFields[field] = {};
        }
        Object.assign(this.editedFields[field], { [subField]: value });
      } else {
        Object.assign(this.editedFields, { [field]: value });
      }
      console.log("Edited fields", this.editedFields);
    },
    removeproductImage(index) {
      console.log(index);
      if (this.productFormData.productImages.length > 0) {
        const removedFile = this.productFormData.productImages[index];
        this.removedImages.push(removedFile);
        this.productFormData.productImages.splice(index, 1);
        this.handleEditedField(true, "productimage");
      }
    },
    handleCroppedImage(croppedCanvas) {
      const fileDetails = this.file;
      croppedCanvas.toBlob((blob) => {
        const croppedFile = new File([blob], fileDetails.name, {
          type: fileDetails.type,
        });

        this.productFormData.productImages.push(croppedFile);
        this.file = null;
        this.showModal = false;
      }, fileDetails.type);
    },
    handleCancel() {
      this.imageUrl = "";
      this.file = null;
      this.showModal = false;
    },
    handleCloseDialog() {
      const fileDetails = this.file;

      if (fileDetails) {
        this.productFormData.productImages.push(fileDetails);
        this.file = null;
        this.imageUrl = "";
      }
      this.showModal = false;
    },
    handleImageUpload(event) {
      this.showModal = false;
      const files = event.target.files;
      this.exceedFileSize = false;
      this.invalidFileType = false;
      this.handleEditedField(true, "productimage");

      for (let file of files) {
        const fileType = file.type;
        if (
          fileType !== "image/jpeg" &&
          fileType !== "image/png" &&
          fileType !== "image/jpg"
        ) {
          this.invalidFileType = true;
          return;
        }

        const filesizeMb = file.size / (1024 * 1024);
        if (filesizeMb > this.maxFileSize) {
          this.exceedFileSize = true;
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result;
          this.file = file;
          this.aspectRatio = 1;
          this.showModal = true;
          event.target.value = "";
        };
        reader.readAsDataURL(file);
      }
    },
    async fetchCategories() {
      const response = await this.getProductCategory();
      if (response.success) {
        this.categories = response.categories;
        console.log(this.categories, "categories");
      } else {
        console.error(response.error);
        alert("Failed to fetch product categories.");
      }
    },
    async fetchSizes() {
      const response = await this.getProductSize();
      if (response.success) {
        this.sizes = response.sizes;
      } else {
        console.error(response.error);
        alert("Failed to fetch product sizes.");
      }
    },
    goback() {
      this.$router.back();
    },
    handleFocus(type = "discription") {
      if (type === "category") this.isCategoryFocused = true;
      this.isDescriptionFocused = true;
    },
    handleBlur(type = "discription") {
      if (type === "category") this.isCategoryFocused = false;
      this.isDescriptionFocused = false;
    },
    addImage() {
      const url = window.prompt("URL");

      if (url) {
        this.editor.chain().focus().setImage({ src: url }).run();
      }
    },
    addVideo() {
      const url = prompt("Enter YouTube URL");

      this.editor.commands.setYoutubeVideo({
        src: url,
        width: Math.max(320, parseInt(this.youtubewidth, 10)) || 640,
        height: Math.max(180, parseInt(this.youtubeheight, 10)) || 480,
      });
    },
    toggleMenu() {
      this.isActive = !this.isActive;
    },
    toggleCategoryMenu() {
      this.isCategoryMenuActive = !this.isCategoryMenuActive;
    },

    handleClickOutside(event) {
      const dropdown = this.$refs.categoryDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.isCategoryMenuActive = false;
      }
      const sizeDropdown = this.$refs.sizeDropdown;
      if (sizeDropdown && !sizeDropdown.contains(event.target)) {
        this.isActive = false;
      }
    },
    selectOption(option) {
      this.selectedOption = option;
      this.isActive = false;
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    this.productId = this.$route.params.id;
    this.fetchProductDetails();
    this.fetchCategories();
    this.fetchSizes();
    this.getCurrency();
    this.editor = new Editor({
      extensions: [
        StarterKit,
        CharacterCount.configure({
          limit: this.limit,
        }),
        Youtube.configure({
          controls: false,
          nocookie: true,
        }),
        Placeholder.configure({
          placeholder: "Product Description",
          emptyNodeClass: "text-gray-600",
        }),
        ListKeymap,
        ListItem,
        Document,
        Image.configure({
          inline: true,
        }),
        Dropcursor,
        Text,
        Typography.configure({
          oneHalf: false,
          oneQuarter: false,
          threeQuarters: false,
        }),
        Paragraph.configure({
          HTMLAttributes: {
            class: "text-gray-600",
          },
        }),
        Bold.configure({
          HTMLAttributes: {
            class: "font-bold",
          },
        }),
        Italic.configure({
          HTMLAttributes: {
            class: "italic",
          },
        }),
        Underline,
        Link.configure({
          HTMLAttributes: {
            class:
              "inline-flex items-center gap-x-1 text-blue-500 decoration-2 hover:underline font-medium",
          },
        }),
        BulletList.configure({
          itemTypeName: "listItem",
          keepMarks: true,
          HTMLAttributes: {
            class: "list-disc text-gray-800",
          },
        }),
        OrderedList.configure({
          HTMLAttributes: {
            class: "list-decimal text-gray-800",
          },
        }),
        Blockquote.configure({
          HTMLAttributes: {
            class: "text-gray-800 sm:text-xl",
          },
        }),
      ],
      editorProps: {
        handlePaste: (view, event) => {
          const clipboardData = event.clipboardData;
          const text = clipboardData.getData("text/plain");
          const html = clipboardData.getData("text/html");

          // Get current content length
          const currentLength = this.editor.storage.characterCount.characters();
          const remainingChars = this.limit - currentLength;

          if (remainingChars <= 0) {
            // No space left - prevent paste
            toast.error(`Character limit of ${this.limit} reached`);
            return true; // Block default paste
          }

          if (html) {
            // For HTML content - extract text and truncate
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            const plainText = doc.body.textContent || "";
            const truncatedText = plainText.slice(0, remainingChars);

            this.editor.commands.insertContent(truncatedText);
            return true;
          }

          if (text) {
            // For plain text - truncate to remaining space
            const truncatedText = text.slice(0, remainingChars);
            this.editor.commands.insertContent(truncatedText);

            // Show warning if text was truncated
            if (text.length > remainingChars) {
              toast.warn(`Pasted content was truncated to fit character limit`);
            }
            return true;
          }

          return false; // Fallback to default behavior
        },
      },
      onUpdate: ({ editor }) => {
        this.productFormData.productDescription = editor.getHTML();
        this.handleEditedField(true, "productdescription");
      },
    });
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    this.editor.destroy();
  },
};
</script>
<style scoped>
.optionsActive {
  animation-name: fadeInUp;
  -webkit-animation-name: fadeInUp;
  animation-duration: 0.4s;
  animation-fill-mode: both;
  -webkit-animation-duration: 0.4s;
  -webkit-animation-fill-mode: both;
}
.tiptap {
  :first-child {
    margin-top: 0;
  }

  /* List styles */
  ul,
  ol {
    padding: 0 1rem;
    margin: 1.25rem 1rem 1.25rem 0.4rem;

    li p {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }
  }

  .character-count {
    align-items: center;
    color: var(--gray-5);
    display: flex;
    gap: 0.5rem;
    margin: 1.5rem;

    svg {
      color: var(--main900);
    }

    &--warning,
    &--warning svg {
      color: var(--red);
    }
  }
  blockquote {
    border-left: 3px solid var(--gray-3);
    margin: 1.5rem 0;
    padding-left: 1rem;
  }
  img {
    display: block;
    height: auto;
    margin: 1.5rem 0;
    max-width: 100%;

    &.ProseMirror-selectednode {
      outline: 3px solid var(--purple);
    }
  }
  div[data-youtube-video] {
    cursor: move;
    padding-right: 1.5rem;

    iframe {
      border: 0.5rem solid var(--black-contrast);
      display: block;
      min-height: 200px;
      min-width: 200px;
      outline: 0px solid transparent;
    }

    &.ProseMirror-selectednode iframe {
      outline: 3px solid var(--purple);
      transition: outline 0.15s;
    }
  }
}
</style>
